import React, { useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL, TOKEN_KEY } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { SectionTitle, StatusPill } from "@/components/dashboard/widgets";
import { Loader2, EyeOff, RotateCcw, Inbox, Search, Download, Filter, X, ShieldCheck, MapPin, Mail, Phone, Building, Calendar, Hash, ArrowUpRight, Share2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = [
  { v: "", label: "All status" },
  { v: "accepted", label: "Accepted" },
  { v: "returned", label: "Returned" },
];
const DATE_PRESETS = [
  { v: "today", label: "Today" },
  { v: "7d", label: "Last 7 days" },
  { v: "30d", label: "Last 30 days" },
  { v: "", label: "All time" },
];

function _isoDaysAgo(n) {
  const d = new Date(); d.setUTCDate(d.getUTCDate() - n); d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}
function _isoStartOfToday() {
  const d = new Date(); d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectors, setSectors] = useState([]);

  // Filters
  const [q, setQ] = useState("");
  const [sector, setSector] = useState("");
  const [status, setStatus] = useState("");
  const [datePreset, setDatePreset] = useState("");

  // Drawer
  const [drawer, setDrawer] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Return flow
  const [returnFor, setReturnFor] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);

  // Load sectors once
  useEffect(() => {
    api.get("/catalog/sectors").then((r) => setSectors(r.data || [])).catch(() => {});
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (sector) params.sector = sector;
      if (status) params.status = status;
      if (datePreset === "today") params.date_from = _isoStartOfToday();
      else if (datePreset === "7d") params.date_from = _isoDaysAgo(7);
      else if (datePreset === "30d") params.date_from = _isoDaysAgo(30);
      const { data } = await api.get("/client/leads", { params });
      setLeads(data || []);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); /* eslint-disable-next-line */ }, [sector, status, datePreset]);
  // Debounced text search
  useEffect(() => {
    const t = setTimeout(fetchLeads, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const openLead = async (l) => {
    setDrawer({ id: l.id }); // immediate open with shell
    setDrawerLoading(true);
    try {
      const { data } = await api.get(`/client/leads/${l.id}`);
      setDrawer(data);
    } catch (e) {
      toast.error("Failed to load lead detail");
      setDrawer(null);
    } finally {
      setDrawerLoading(false);
    }
  };

  const hide = async (l, e) => {
    e?.stopPropagation();
    if (!window.confirm("Hide this lead from your view? Publisher remains paid.")) return;
    setBusy(l.id);
    try {
      await api.post(`/client/leads/${l.id}/hide`);
      toast.success("Lead hidden from view");
      fetchLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const submitReturn = async () => {
    if (!returnFor) return;
    if (!reason.trim()) { toast.error("Please add a quality reason"); return; }
    setBusy(returnFor.id);
    try {
      const { data } = await api.post(`/client/leads/${returnFor.id}/return`, { reason: reason.trim() });
      toast.success(`Lead returned · £${(data.refunded_gbp || 0).toFixed(2)} refunded`);
      setReturnFor(null); setReason("");
      setDrawer(null);
      fetchLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (sector) params.set("sector", sector);
    if (status) params.set("status", status);
    if (datePreset === "today") params.set("date_from", _isoStartOfToday());
    else if (datePreset === "7d") params.set("date_from", _isoDaysAgo(7));
    else if (datePreset === "30d") params.set("date_from", _isoDaysAgo(30));
    // Token via short-lived signed URL would be cleaner; for now we open with header via fetch + blob
    fetch(`${BACKEND_URL}/api/client/leads-export.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error("CSV failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `connexis_leads.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("CSV export failed"));
  };

  const clearFilters = () => { setQ(""); setSector(""); setStatus(""); setDatePreset(""); };
  const anyFilter = q || sector || status || datePreset;

  return (
    <div className="space-y-5">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <SectionTitle title="Leads" subtitle="All accepted leads. Click a row for the full profile · Return refunds your wallet · Hide just removes from view." />
        <div className="flex items-center gap-2">
          <Button onClick={exportCsv} variant="outline" className="rounded-full h-9" data-testid="leads-export-csv">
            <Download className="size-4" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="rounded-xl shadow-sm border-slate-200">
        <CardContent className="p-3 sm:p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2.5">
            {/* Search */}
            <div className="col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-slate-400" />
              <Input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email, phone…"
                className="pl-9 h-9 text-[13px]"
                data-testid="leads-search"
              />
            </div>
            {/* Sector */}
            <select value={sector} onChange={(e) => setSector(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0055FF]/15 focus:border-[#0055FF]"
              data-testid="leads-filter-sector">
              <option value="">All sectors</option>
              {sectors.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
            </select>
            {/* Status */}
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-[13px]"
              data-testid="leads-filter-status">
              {STATUS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            {/* Date */}
            <select value={datePreset} onChange={(e) => setDatePreset(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-[13px]"
              data-testid="leads-filter-date">
              {DATE_PRESETS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            {/* Clear */}
            {anyFilter && (
              <Button variant="ghost" onClick={clearFilters} className="rounded-md h-9 text-[12px] text-slate-500" data-testid="leads-filter-clear">
                <X className="size-3.5" /> Clear
              </Button>
            )}
          </div>
          {anyFilter && (
            <div className="mt-2.5 text-[11.5px] text-slate-500 flex items-center gap-1.5">
              <Filter className="size-3" /> Showing <b className="text-slate-800">{leads.length}</b> filtered lead{leads.length === 1 ? "" : "s"}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      {loading ? (
        <Card className="rounded-xl border-slate-200">
          <CardContent className="p-12 text-center text-slate-500"><Loader2 className="size-5 animate-spin mx-auto" /></CardContent>
        </Card>
      ) : leads.length === 0 ? (
        <Card className="rounded-xl border-dashed border-slate-200">
          <CardContent className="p-12 text-center text-slate-500">
            <Inbox className="size-10 mx-auto mb-3 text-slate-300" />
            <div className="font-medium text-slate-700">{anyFilter ? "No leads match your filters" : "No leads yet"}</div>
            <div className="text-sm mt-1">
              {anyFilter ? "Try widening your search." : "Once campaigns are live and publishers ingest leads matching your sector, they'll appear here in real-time."}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-xl shadow-soft border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Lead</th>
                  <th className="font-medium">Sector</th>
                  <th className="font-medium">Status</th>
                  <th className="font-medium">Received</th>
                  <th className="font-medium text-right">Price</th>
                  <th className="font-medium text-right pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    className="border-t border-slate-100 hover:bg-slate-50/50 cursor-pointer"
                    onClick={() => openLead(l)}
                    data-testid={`lead-row-${l.id.slice(0, 8)}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{l.first_name} {l.last_name}</div>
                      <div className="text-[11.5px] text-slate-500">{l.email} · {l.phone || "—"}</div>
                    </td>
                    <td className="capitalize text-slate-700">{(l.sector || "").replace(/-/g, " ")}</td>
                    <td><StatusPill status={l.status} /></td>
                    <td className="text-slate-600 text-[12.5px]">{new Date(l.created_at).toLocaleString("en-GB")}</td>
                    <td className="text-right font-mono tabular-nums text-slate-900">£{Number(l.price || 0).toFixed(2)}</td>
                    <td className="pr-4 py-3 text-right">
                      {l.status === "returned" ? (
                        <span className="text-[11.5px] text-slate-500 italic">Returned</span>
                      ) : (
                        <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5 text-[11.5px] border-amber-200 text-amber-700 hover:bg-amber-50" disabled={busy === l.id} onClick={(e) => { e.stopPropagation(); setReturnFor(l); setReason(""); }} data-testid={`lead-return-${l.id.slice(0, 8)}`}>
                            <RotateCcw className="size-3" /> Return
                          </Button>
                          <Button size="sm" variant="ghost" className="rounded-full h-7 px-2.5 text-[11.5px]" disabled={busy === l.id} onClick={(e) => hide(l, e)} data-testid={`lead-hide-${l.id.slice(0, 8)}`}>
                            <EyeOff className="size-3" /> Hide
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Detail drawer */}
      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="lead-drawer">
          {drawerLoading || !drawer?.lead ? (
            <div className="py-20 flex items-center justify-center text-slate-500">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : (
            <LeadDetailBody
              detail={drawer}
              onReturn={() => { setReturnFor(drawer.lead); setReason(""); }}
              onHide={(e) => hide(drawer.lead, e)}
              busy={busy === drawer.lead.id}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Return dialog */}
      <Dialog open={!!returnFor} onOpenChange={(o) => !o && setReturnFor(null)}>
        <DialogContent className="max-w-md" data-testid="lead-return-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RotateCcw className="size-5 text-amber-600" /> Return lead (quality issue)</DialogTitle>
            <DialogDescription>The lead price (£{Number(returnFor?.price || 0).toFixed(2)}) will be refunded to your wallet. The publisher's earning is clawed back and an audit event is logged.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-[12.5px] text-slate-500">Lead: <b className="text-slate-900">{returnFor?.first_name} {returnFor?.last_name}</b> · {returnFor?.email}</div>
            <Textarea rows={4} placeholder="What was wrong with this lead? (required — used for publisher quality scoring)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="lead-return-reason" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReturnFor(null)} className="rounded-full">Cancel</Button>
            <Button onClick={submitReturn} disabled={busy === returnFor?.id || !reason.trim()} className="rounded-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="lead-return-submit">
              {busy === returnFor?.id ? <><Loader2 className="size-4 animate-spin" /> Returning…</> : "Confirm return"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═════════════════ Lead detail drawer body ═════════════════ */
function LeadDetailBody({ detail, onReturn, onHide, busy }) {
  const l = detail.lead;
  const pub = detail.publisher;
  const c = detail.compliance || {};
  const r = detail.routing || {};
  const replay = detail.replay;
  const fullName = `${l.first_name || ""} ${l.last_name || ""}`.trim() || "—";
  const isReturned = l.status === "returned";

  return (
    <>
      <SheetHeader className="pb-3 border-b border-slate-100">
        <div className="flex items-baseline justify-between">
          <SheetTitle className="text-xl">{fullName}</SheetTitle>
          <StatusPill status={l.status} />
        </div>
        <SheetDescription className="font-mono text-[11.5px] text-slate-500">{l.id}</SheetDescription>
      </SheetHeader>

      <div className="space-y-5 py-5 text-[13px] text-slate-700">
        {/* Contact */}
        <Block title="Contact">
          <Row Icon={Mail}     label="Email"    value={l.email} mono />
          <Row Icon={Phone}    label="Phone"    value={l.phone || "—"} mono />
          <Row Icon={MapPin}   label="Postcode" value={l.postcode || "—"} />
          <Row Icon={Building} label="Sector"   value={(l.sector || "").replace(/-/g, " ")} cap />
        </Block>

        {/* Routing journey */}
        <Block title="Routing journey">
          <Row Icon={Hash}     label="Publisher"  value={pub?.name || "—"} />
          <Row Icon={Hash}     label="Campaign"   value={r.campaign_id || "—"} mono />
          {r.lead_pack_id && <Row Icon={Hash} label="Lead pack" value={r.lead_pack_id} mono />}
          <Row Icon={Calendar} label="Delivered"  value={r.delivered_at ? new Date(r.delivered_at).toLocaleString("en-GB") : "—"} />
          <Row Icon={Hash}     label="Price paid" value={`£${Number(l.price || 0).toFixed(2)}`} mono />
        </Block>

        {/* Compliance */}
        <Block title="Compliance attestation" Icon={ShieldCheck}>
          <Row label="Consent method"    value={c.consent_method || "TrustedForm"} />
          <Row label="Consent timestamp" value={c.consent_timestamp ? new Date(c.consent_timestamp).toLocaleString("en-GB") : "—"} />
          <Row label="TrustedForm token" value={c.consent_token ? c.consent_token.slice(0, 32) + "…" : "—"} mono />
          <Row label="IP address"        value={c.ip || "—"} mono />
          <Row label="User agent"        value={c.user_agent ? c.user_agent.slice(0, 64) + (c.user_agent.length > 64 ? "…" : "") : "—"} mono />
        </Block>

        {/* Replay (AI Voice/Chat) */}
        {replay && (
          <Block title="AI conversation replay">
            <div className="flex items-center gap-2 flex-wrap">
              <a href={replay.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#0055FF] hover:underline text-[13px]">
                Open replay <ArrowUpRight className="size-3.5" />
              </a>
              <button
                onClick={() => {
                  const url = `${(process.env.REACT_APP_SITE_URL || "https://connex.is").replace(/\/+$/, "")}${replay.url}`;
                  navigator.clipboard.writeText(url);
                  toast.success("Replay link copied — share with your team");
                }}
                className="ml-auto inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50 text-slate-700"
                data-testid="drawer-share-replay"
              >
                <Share2 className="size-3" /> Share replay
              </button>
            </div>
            <div className="mt-1.5 text-[11px] text-slate-500">Anyone with the link can view this AI conversation. No PII beyond first-name initial is exposed.</div>
          </Block>
        )}

        {/* Return reason if applicable */}
        {isReturned && l.returned_reason && (
          <Block title="Return reason">
            <div className="rounded-md bg-amber-50/50 border border-amber-200 text-[13px] text-amber-900 px-3 py-2">
              {l.returned_reason}
            </div>
          </Block>
        )}
      </div>

      {!isReturned && (
        <div className="border-t border-slate-100 pt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onHide} disabled={busy} className="rounded-full" data-testid="drawer-hide-btn">
            <EyeOff className="size-3.5" /> Hide
          </Button>
          <Button onClick={onReturn} disabled={busy} className="rounded-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="drawer-return-btn">
            <RotateCcw className="size-3.5" /> Return lead
          </Button>
        </div>
      )}
    </>
  );
}

function Block({ title, Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2.5 text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">
        {Icon && <Icon className="size-3 text-[#0055FF]" />}
        {title}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ Icon, label, value, mono, cap }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[12px] text-slate-500 flex items-center gap-1.5 shrink-0">
        {Icon && <Icon className="size-3 text-slate-400" />}
        {label}
      </span>
      <span className={`text-[13px] text-slate-900 text-right ${mono ? "font-mono tabular-nums" : ""} ${cap ? "capitalize" : ""} truncate`}>{value}</span>
    </div>
  );
}
